/**
 * The casino REST surface, as the clients use it. The code lookup, the
 * timeline and the leaderboard are public — a TV has no session to offer;
 * a member's own card is opened with the phone's `memberToken`, and the
 * owner calls carry the session cookie via `credentials: 'include'`.
 */
import type {
  BalanceTimelineResponse,
  CasinoLeaderboardEntry,
  CasinoLookup,
  CasinoMemberRow,
  CasinoMemberView,
  CasinoTableSummary,
  CasinoTheme,
  GameType,
  LedgerTapeResponse,
  MemberStatsResponse,
  ProfileColorId,
  ProfilePlayResponse,
  ReferralSummary,
  RoundHistoryResponse,
  StatsWindow,
} from '@tv-casino/shared';

export type { CasinoLookup, CasinoMemberRow, CasinoTableSummary };

const message = async (res: Response, fallback: string): Promise<string> => {
  try {
    const body = (await res.json()) as { message?: string };
    return body.message ?? fallback;
  } catch {
    return fallback;
  }
};

/** Throws with the server's own wording; callers show it verbatim. */
async function get<T>(path: string, fallback: string, credentials = false): Promise<T> {
  const res = await fetch(path, credentials ? { credentials: 'include' } : undefined);
  if (!res.ok) throw new Error(await message(res, fallback));
  return (await res.json()) as T;
}

export const lookupCasino = (code: string): Promise<CasinoLookup> =>
  get(`/api/casinos/${encodeURIComponent(code)}`, 'No casino with that code.');

/** One row from `GET /api/casinos` — the venues this session user owns. */
export interface OwnedCasinoSummary {
  id: string;
  name: string;
  joinCode: string;
}

/**
 * The signed-in owner's casinos. An anonymous or failed read is an empty
 * list, never an error — Play and the TV treat that as "not the owner".
 */
export async function listOwnedCasinos(): Promise<OwnedCasinoSummary[]> {
  const res = await fetch('/api/casinos', { credentials: 'include' });
  if (!res.ok) return [];
  const data: unknown = await res.json().catch(() => null);
  return Array.isArray(data) ? (data as OwnedCasinoSummary[]) : [];
}

/** Open a venue under the signed-in account. The server creates its first table with it. */
export async function createCasino(name: string): Promise<OwnedCasinoSummary> {
  const res = await fetch('/api/casinos', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(await message(res, 'Could not create the casino.'));
  return (await res.json()) as OwnedCasinoSummary;
}

/**
 * A member's own card, proved with the token the membership was opened with.
 * Resolves to null before the membership exists — a phone that has scanned
 * the QR but not sat down yet — which the lobby renders as the newcomer
 * state rather than an error, and null again when this phone cannot prove
 * the membership is its own.
 *
 * The session cookie rides along for the one member who has no token: the
 * owner, whose own seat is keyed to their account.
 */
export async function fetchMemberView(
  code: string,
  memberId: string,
  memberToken: string | null,
): Promise<CasinoMemberView | null> {
  const res = await fetch(
    `/api/casinos/${encodeURIComponent(code)}/me/${encodeURIComponent(memberId)}`,
    {
      credentials: 'include',
      headers: memberToken ? { 'x-member-token': memberToken } : undefined,
    },
  );
  if (res.status === 404 || res.status === 401) return null;
  if (!res.ok) throw new Error(await message(res, 'Could not read your chips.'));
  return (await res.json()) as CasinoMemberView;
}

export const fetchLeaderboard = (code: string, window: StatsWindow = 'all'): Promise<CasinoLeaderboardEntry[]> =>
  get(
    `/api/casinos/${encodeURIComponent(code)}/leaderboard?window=${window}`,
    'Could not read the leaderboard.',
  );

export const fetchMembers = (code: string): Promise<CasinoMemberRow[]> =>
  get(`/api/casinos/${encodeURIComponent(code)}/members`, 'Could not read the members.', true);

export const fetchMemberStats = (code: string, memberId: string): Promise<MemberStatsResponse> =>
  get(
    `/api/casinos/${encodeURIComponent(code)}/members/${encodeURIComponent(memberId)}/stats`,
    'Could not read those stats.',
    true,
  );

/** Owner only: a member's settled rounds, newest first. */
export const fetchMemberHistory = (
  code: string,
  memberId: string,
  limit = 50,
): Promise<RoundHistoryResponse> =>
  get(
    `/api/casinos/${encodeURIComponent(code)}/members/${encodeURIComponent(memberId)}/history?limit=${limit}`,
    'Could not read that history.',
    true,
  );

/** Owner only: a member's raw chip tape, newest first. */
export const fetchMemberLedger = (
  code: string,
  memberId: string,
  limit = 50,
): Promise<LedgerTapeResponse> =>
  get(
    `/api/casinos/${encodeURIComponent(code)}/members/${encodeURIComponent(memberId)}/ledger?limit=${limit}`,
    'Could not read that ledger.',
    true,
  );

/**
 * A phone's own trade blotter / chip tape, proved with the membership token
 * the same way `/me` is. Resolves to null when this phone cannot prove the
 * membership — the desk stays quiet rather than erroring the lobby.
 */
async function fetchMyAudit<T>(
  code: string,
  memberId: string,
  memberToken: string | null,
  path: 'history' | 'ledger',
  limit: number,
  fallback: string,
): Promise<T | null> {
  const res = await fetch(
    `/api/casinos/${encodeURIComponent(code)}/me/${encodeURIComponent(memberId)}/${path}?limit=${limit}`,
    {
      credentials: 'include',
      headers: memberToken ? { 'x-member-token': memberToken } : undefined,
    },
  );
  if (res.status === 404 || res.status === 401) return null;
  if (!res.ok) throw new Error(await message(res, fallback));
  return (await res.json()) as T;
}

/**
 * The member's own profile edits — their name at this venue and the hue it
 * wears — proved the same way `/me` is. Answers the refreshed card, so the
 * caller never has to re-read what it just wrote.
 */
export async function updateMyMembership(
  code: string,
  memberId: string,
  memberToken: string | null,
  patch: { name?: string; color?: ProfileColorId | null },
): Promise<CasinoMemberView> {
  const res = await fetch(
    `/api/casinos/${encodeURIComponent(code)}/me/${encodeURIComponent(memberId)}`,
    {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        ...(memberToken ? { 'x-member-token': memberToken } : {}),
      },
      body: JSON.stringify(patch),
    },
  );
  if (!res.ok) throw new Error(await message(res, 'Could not save your profile.'));
  return (await res.json()) as CasinoMemberView;
}

/**
 * Every venue this signed-in account has played, with the cross-casino fold.
 * Signed out it is an empty history rather than an error — the profile still
 * has a membership card to show.
 */
export async function fetchMyPlay(): Promise<ProfilePlayResponse> {
  const res = await fetch('/api/me/play', { credentials: 'include' });
  if (!res.ok) throw new Error(await message(res, 'Could not read your play history.'));
  return (await res.json()) as ProfilePlayResponse;
}

/** The account's referral code and its counts. Session only. */
export async function fetchMyReferrals(): Promise<ReferralSummary> {
  const res = await fetch('/api/me/referrals', { credentials: 'include' });
  if (!res.ok) throw new Error(await message(res, 'Could not read your referrals.'));
  return (await res.json()) as ReferralSummary;
}

export const fetchMyHistory = (
  code: string,
  memberId: string,
  memberToken: string | null,
  limit = 50,
): Promise<RoundHistoryResponse | null> =>
  fetchMyAudit(code, memberId, memberToken, 'history', limit, 'Could not read your rounds.');

export const fetchMyLedger = (
  code: string,
  memberId: string,
  memberToken: string | null,
  limit = 50,
): Promise<LedgerTapeResponse | null> =>
  fetchMyAudit(code, memberId, memberToken, 'ledger', limit, 'Could not read your ledger.');

/** Public, like the leaderboard: every member's balance line for the window. */
export const fetchBalanceTimeline = (code: string, window: StatsWindow = 'all'): Promise<BalanceTimelineResponse> =>
  get(
    `/api/casinos/${encodeURIComponent(code)}/timeline?window=${window}`,
    'Could not read the night.',
    false,
  );

/** Owner only: grant (positive) or revoke (negative) chips. Always a ledger row. */
export async function grantChips(
  code: string,
  memberId: string,
  amount: number,
): Promise<CasinoMemberRow> {
  const res = await fetch(
    `/api/casinos/${encodeURIComponent(code)}/members/${encodeURIComponent(memberId)}/grant`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ amount }),
    },
  );
  if (!res.ok) throw new Error(await message(res, 'The grant was refused.'));
  return (await res.json()) as CasinoMemberRow;
}

/**
 * Owner only: dress the venue. Send just the part that changed; the server
 * lays it over the current theme. A TV already on a table repaints from the
 * `casino_state` push; a phone picks it up on its next read of the lineup.
 */
export async function setCasinoTheme(code: string, theme: Partial<CasinoTheme>): Promise<void> {
  const res = await fetch(`/api/casinos/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ theme }),
  });
  if (!res.ok) throw new Error(await message(res, 'Could not change the felt.'));
}

/**
 * Owner only: hide or show the venue's join code. Every screen follows the
 * `casino_state` push, so a TV showing the QR loses it the moment this lands.
 */
export async function setCasinoPrivate(code: string, isPrivate: boolean): Promise<void> {
  const res = await fetch(`/api/casinos/${encodeURIComponent(code)}`, {
    method: 'PATCH',
    headers: { 'content-type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ isPrivate }),
  });
  if (!res.ok) throw new Error(await message(res, 'Could not change the venue privacy.'));
}

/** Owner only: remove a table. A table on the TV takes its live room down. */
export async function deleteTable(code: string, tableId: string): Promise<void> {
  const res = await fetch(
    `/api/casinos/${encodeURIComponent(code)}/tables/${encodeURIComponent(tableId)}`,
    { method: 'DELETE', credentials: 'include' },
  );
  if (!res.ok) throw new Error(await message(res, 'Could not remove the table.'));
}

/**
 * Owner only: close the venue for good. Tables, memberships, chips and the
 * whole history go with it.
 */
export async function deleteCasino(code: string): Promise<void> {
  const res = await fetch(`/api/casinos/${encodeURIComponent(code)}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  if (!res.ok) throw new Error(await message(res, 'Could not close the casino.'));
}
